import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';

/**
 * NoteEditor - Form for adding/editing notes with voice recognition support
 * @param {string} noteText - Current note text
 * @param {Function} onNoteTextChange - Callback when note text changes
 * @param {Function} onSave - Callback when save is clicked
 * @param {Function} onCancel - Callback when cancel is clicked (edit mode only)
 * @param {boolean} saving - Whether note is being saved
 * @param {boolean} isEditing - Whether in edit mode
 * @param {boolean} isListening - Whether speech recognition is listening
 * @param {Function} onStopListening - Callback to stop speech recognition
 * @param {Function} onFocus - Callback when text field is focused
 * @param {Function} onBlur - Callback when text field loses focus
 */
export default function NoteEditor({
  noteText,
  onNoteTextChange,
  onSave,
  onCancel,
  saving,
  isEditing,
  isListening,
  onStopListening,
  onFocus,
  onBlur
}) {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 0 }}>
          <strong>{isEditing ? 'Edit Note:' : 'Add Note:'}</strong>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isListening && (
            <>
              <MicIcon sx={{ color: 'error.main', fontSize: 20, animation: 'pulse 1.5s ease-in-out infinite' }} />
              <Typography variant="caption" color="error.main">
                Listening...
              </Typography>
              <IconButton
                size="small"
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent TextField from losing focus on desktop
                }}
                onTouchStart={(e) => {
                  e.preventDefault(); // Prevent TextField from losing focus on mobile
                }}
                onClick={(e) => {
                  e.preventDefault(); // Prevent any default behavior
                  onStopListening();
                }}
                sx={{
                  color: 'error.main',
                  padding: '2px',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.08)'
                  }
                }}
                title="Stop Listening"
              >
                <StopCircleIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      <TextField
        fullWidth
        multiline
        rows={4}
        value={noteText}
        onChange={(e) => onNoteTextChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={isEditing ? "Edit your note..." : "Write a note about this match..."}
        variant="outlined"
        size="small"
        sx={{ mt: 1 }}
      />
      <Box sx={{ mt: 1, mb: 2, display: 'flex', gap: 1 }}>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={saving || !noteText.trim()}
          size="small"
        >
          {saving ? 'Saving...' : (isEditing ? 'Update Note' : 'Save Note')}
        </Button>
        {isEditing && (
          <Button
            onClick={onCancel}
            variant="outlined"
            disabled={saving}
            size="small"
          >
            Cancel
          </Button>
        )}
      </Box>
    </>
  );
}
